import Request from '../../utils/request';
import {
  Context,
  WSEventType,
  WSElementType,
  JS_ERROR,
  SondaType,
  Sonda as SondaInterface,
  MethodType,
  UnknownObject,
} from '../../utils/types';

export const createJSErrorContext = (segment: string): unknown => {
  let clientIDs = null;

  if (window.IC && window.IC.getClientIds) {
    clientIDs = window.IC.getClientIds();
  }

  const context = {
    pageSegment: segment || '',
    contextURL: window.location.href || '',
    clientIDs,
  };

  return context;
};

export default class Sonda implements SondaInterface {
  eventName: string;

  origin: string;

  context: Context;

  uniqueId: string;

  /**
  * @param {string} eventName nombre del servicio/funciona sondar ej: 'CUSTOMIZER.ACTVITY.GETBYNAME'
  * @param {Context} context Contexto
  * @param {string} uniqueId Identificador unico tipo UUID, para los servicios es obligatorio, para sondar un js no.
  * @param {object} origin Desde donde se utilizan las sondas
  */
  constructor({
    eventName,
    context,
    uniqueId,
    origin,
  }: {
    eventName: string;
    context: Context;
    uniqueId?: string;
    origin?: string;
  }) {
    this.eventName = eventName;
    this.origin = origin || 'ADABYRON-API';
    this.context = context;
    this.uniqueId = uniqueId;
  }

  getFinalSondaInfo = (
    cData,
    type,
    eventName,
    wsEventType,
    elementType,
  ): UnknownObject => {
    let sondaInfo = {};

    switch (elementType) {
      case JS_ERROR:
        sondaInfo = {
          cData,
          cdContextInfo: '',
          cdElementType: elementType || '',
          cdEnvironment: 'WEB',
          cdEventName: '',
          cdEventType: '',
          // completamos el referer ?
          cdReferer: '',
          cdSegment: (this.context && this.context.pageSegment) || 'no_def',
          cdSource: 'MonitoringUtils',
          cdTimeStamp: new Date().toISOString(),
          cdType: 'Error-Handler',
          cdUrl: (this.context && this.context.contextURL) || '',
          ctzActivityId: '',
          ctzExperienceId: '',
          ctzId: '',
          ctzPageId: '',
          ctzTemplateId: '',
          phCode: '',
          posCode: '',
          status: '',
          uiGAClientId:
            (this.context &&
              this.context.clientIDs &&
              this.context.clientIDs.ga) ||
            '',
          wbPageId:
            (this.context &&
              this.context.clientIDs &&
              this.context.clientIDs.pageId) ||
            '',
          wbSessionId:
            (this.context &&
              this.context.clientIDs &&
              this.context.clientIDs.sessId) ||
            '',
          webCode: '',
          wsContext: this.origin, // url de js o url html: webpack://src/{nombreJS}
          wsName: this.eventName, // linea
        };
        break;
      default:
        sondaInfo = {
          // Traza
          wbSessionId:
            (this.context &&
              this.context.clientIDs &&
              this.context.clientIDs.sessId) ||
            '',
          uiGAClientId:
            (this.context &&
              this.context.clientIDs &&
              this.context.clientIDs.ga) ||
            '',

          // Contextual
          cdContext: this.origin,
          cdSource: 'MonitoringUtils',
          cdEnvironment: 'WEB',
          cdSegment: (this.context && this.context.pageSegment) || 'no_def',
          cdUserAgent: this.context && this.context.userAgent,
          cdTimeStamp: new Date(),
          cdUrl: (this.context && this.context.contextURL) || '',
          cdReferer: (this.context && this.context.referalAP) || '',

          // Evento
          // -> Identificador único.
          ctzId: this.uniqueId,
          // -> Tipo ('ws' | 'chat' | 'ctz').
          cdType: type || '',
          // -> Nombre del evento.
          cdEventName: eventName || '',
          // -> Tipo de Evento solo para WebServices ('Started' | 'Loaded').
          cdEventType: wsEventType || '',
          // -> Estado del Evento WebService ('ok', 'error', 'js-error', 'error-timeout').
          cdElementType: elementType || '',
          // -> Información adicional (datos)
          cData: JSON.stringify(cData),
          // -> Contexto del WebService
          wsContext: this.origin,
          // --> Nombre del WebService (El mismo que el del Evento)
          wsName: eventName || '',
          // --> Estado del WebService (El mismo que el WSElementType)
          status: elementType || '',
        };
    }
    return sondaInfo;
  };

  /**
   * Realiza la petición al endpoint de sondas
   * @param {SondaType} type
   * @param {string} eventName Nombre del evento
   * @param {unknown} info Información asociada al evento que se está sondando
   * @param {WSEventType} wsEventType
   * @param {(WSElementType|string)} elementType -
   */
  sendInfoToCtzMetricsData(
    type: SondaType,
    eventName: string,
    info?: unknown,
    wsEventType?: WSEventType,
    elementType?: WSElementType | string,
  ): void {
    try {
      const endPoint =
        'https://europe-west1-mov-prod10.cloudfunctions.net/export-customizer-data-metrics';
      const headers = {
        timeout: 6000,
        'Content-Type': 'application/json',
      };

      let cData: any = { idPeticion: this.uniqueId };

      if (info && typeof info === 'object') {
        cData = {
          ...cData,
          ...info,
        };
      } else {
        cData.info = info;
      }

      const sondaInfo = this.getFinalSondaInfo(
        cData,
        type,
        eventName,
        wsEventType,
        elementType,
      );

      const request = new Request({
        method: MethodType.POST,
        endpoint: endPoint,
        data: sondaInfo,
        headers,
      });

      request.makeRequest();
    } catch (e) {
      console.error('Sonda.sendInfoToCtzMetricsData - ', e);
      this.errorJs(e);
    }
  }

  /**
   * Indica el inicio de solicitud de un WebService/EndPoint invocado desde frontal
   * @param {unknown} info Información asociada al evento que se está sondando
   * @param {Type} type
   */
  startWS(info?: unknown): void {
    this.sendInfoToCtzMetricsData(
      SondaType.WS,
      this.eventName,
      info,
      WSEventType.STARTED,
    );
  }

  /**
   * Indica la finalización de solicitud de un WebService/EndPoint invocado desde frontal
   * @param {WSElementType} status
   * @param {unknown} info Información adicional a enviar
   * @param {Type} type
   */
  endWS(status: WSElementType, info?: unknown): void {
    this.sendInfoToCtzMetricsData(
      SondaType.WS,
      this.eventName,
      info,
      WSEventType.LOADED,
      status,
    );
  }

  /**
   * Indica el inicio de solicitud de un WebService/EndPoint invocado desde frontal
   * @param {unknown} info Información asociada al evento que se está sondando
   * @param {Type} type
   */
  startExternalFlow(info?: unknown): void {
    this.sendInfoToCtzMetricsData(
      SondaType.EXTERNAL_FLOW,
      this.eventName,
      info,
      WSEventType.STARTED,
    );
  }

  /**
   * Indica la finalización de solicitud de un WebService/EndPoint invocado desde frontal
   * @param {WSElementType} status
   * @param {unknown} info Información adicional a enviar
   * @param {Type} type
   */
  endExternalFlow(status: WSElementType, info?: unknown): void {
    this.sendInfoToCtzMetricsData(
      SondaType.EXTERNAL_FLOW,
      this.eventName,
      info,
      WSEventType.LOADED,
      status,
    );
  }

  /**
   * Envía información de un evento concreto a las sondas
   * @param {unknown} info - Información adicional del evento, puede ser un JSON, un String...
   * @param {Type} type
   */
  sondaEvent(info: unknown, type: SondaType = SondaType.EVENT): void {
    this.sendInfoToCtzMetricsData(type, this.eventName, info);
  }

  /**
   * Envía información de un error concreto a las sondas
   * @param jsError Excepción tipicamente capturada en los catch de JS
   *
   */
  errorJs(jsError: any): void {
    try {
      if (jsError !== undefined) {
        const errorInfo = `${
          typeof jsError.stack !== 'undefined' ? jsError.stack : jsError
        }`;
        this.sendInfoToCtzMetricsData(
          SondaType.ERROR_HANDLER,
          this.context.contextURL,
          errorInfo,
          null,
          JS_ERROR,
        );
      }
    } catch (e) {
      console.error('Sonda.errorJs - ', e);
    }
  }
}
